import { authClient } from "@/api/auth-client";
import { trpc } from "@/api/trpc";
import { Can } from "@/shared/rbac";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ROLE_LABELS_CS } from "@template/shared";
import {
  Banknote,
  Box,
  Building2,
  Calendar,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Compass,
  FileText,
  FolderOpen,
  GitBranch,
  GraduationCap,
  History,
  Inbox,
  LayoutDashboard,
  LineChart,
  ListChecks,
  LogOut,
  Mail,
  MessageSquare,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Send,
  Settings,
  Tag,
  TrendingUp,
  Users,
  Users2,
  Zap,
} from "lucide-react";
import { SidebarNavLink } from "./sidebar-nav-link";
import { SidebarSection } from "./sidebar-section";
import { SidebarCollapsedContext, useSidebarStore } from "./sidebar-store";

interface SidebarProps {
  onNavigate?: (() => void) | undefined;
}

/**
 * Hlavní navigace v dashboardu. Tmavá slate paleta — nemění se s light/dark režimem.
 * Položky se zobrazují podle RBAC permissions (přes Can wrapper).
 */
export function Sidebar({ onNavigate }: SidebarProps) {
  const sessionQuery = trpc.auth.getSession.useQuery();
  const profile = sessionQuery.data?.profile;
  const userEmail = sessionQuery.data?.user?.email;
  const roleLabel = profile ? ROLE_LABELS_CS[profile.role] : "";
  const displayName = profile?.name ?? userEmail ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Sbalení platí jen pro desktop — mobilní sheet (onNavigate) je vždy plný.
  const storeCollapsed = useSidebarStore((s) => s.collapsed);
  const toggleCollapsed = useSidebarStore((s) => s.toggle);
  const collapsed = storeCollapsed && !onNavigate;

  const handleLogout = async () => {
    await authClient.signOut();
    // Vyčistit SPA cache — jinak by po přihlášení jiného uživatele ve stejné
    // kartě mohly z cache uniknout data předchozího (sazby, notifikace…).
    queryClient.clear();
    navigate({ to: "/login" });
  };

  return (
    <SidebarCollapsedContext.Provider value={collapsed}>
      <aside
        className={`flex h-full ${collapsed ? "w-[68px]" : "w-[260px]"} flex-col bg-[hsl(var(--sidebar-bg))] text-[hsl(var(--sidebar-fg))]`}
        data-testid="app-sidebar"
      >
        {/* Brand */}
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className={`flex items-center gap-3 border-b border-[hsl(var(--sidebar-divider))] py-3.5 ${collapsed ? "justify-center px-2" : "px-4"}`}
          data-testid="app-sidebar-brand"
          title={collapsed ? "HCA Moravia — Přehled" : undefined}
        >
          {/* Skutečné HCA logo (navy) na světlé dlaždici — oficiální lockup. Dlaždice
            má pevnou velikost, takže větší logo nerozšíří 260px sidebar. */}
          <div
            className={`flex ${collapsed ? "h-10 w-10" : "h-12 w-12"} flex-shrink-0 items-center justify-center rounded-md bg-[color:var(--hca-ivory)] p-1.5 shadow-sm`}
          >
            <img src="/hca-logo.png" alt="HCA Moravia" className="h-full w-full object-contain" />
          </div>
          {collapsed ? null : (
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-semibold text-[hsl(var(--sidebar-fg))] text-base leading-tight">
                HCA Moravia
              </span>
              <span className="text-[0.72rem] text-[hsl(var(--sidebar-fg-muted))] leading-tight">
                Vzdělávací systém
              </span>
            </div>
          )}
        </Link>

        {/* Sbalení — jen na desktopu (mobilní sheet zavírá křížkem). */}
        {onNavigate ? null : (
          <button
            type="button"
            onClick={toggleCollapsed}
            className={`flex items-center gap-2 border-b border-[hsl(var(--sidebar-divider))] py-2 text-[hsl(var(--sidebar-fg-muted))] text-xs transition-colors hover:bg-[hsl(var(--sidebar-bg-elevated))] hover:text-[hsl(var(--sidebar-fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sidebar-active-bg))] ${collapsed ? "justify-center px-2" : "px-4"}`}
            aria-label={collapsed ? "Rozbalit navigaci" : "Sbalit navigaci na ikony"}
            title={collapsed ? "Rozbalit navigaci" : "Sbalit navigaci na ikony"}
            data-testid="app-sidebar-collapse-toggle"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
                <span>Sbalit</span>
              </>
            )}
          </button>
        )}

        {/* Scrollable nav */}
        <nav
          className="min-h-0 flex-1 overflow-y-auto px-2 py-2"
          aria-label="Hlavní navigace"
          data-testid="app-sidebar-nav"
        >
          {/* Přehled — bez sekce, vždy úplně nahoře. */}
          <Can permission="hca.dashboard.read_ops">
            <SidebarNavLink
              to="/dashboard"
              label="Přehled"
              icon={LayoutDashboard}
              testId="sidebar-nav-dashboard"
              onNavigate={onNavigate}
            />
          </Can>

          <SidebarSection label="Semináře">
            <Can permission="hca.terminy.read">
              <SidebarNavLink
                to="/dashboard/terminy"
                label="Termíny"
                icon={Calendar}
                testId="sidebar-nav-terminy"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.dochazka.read">
              <SidebarNavLink
                to="/dashboard/dochazka"
                label="Docházka"
                icon={ClipboardCheck}
                testId="sidebar-nav-dochazka"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.products.read">
              <SidebarNavLink
                to="/dashboard/checklists"
                label="Checklisty"
                icon={ListChecks}
                testId="sidebar-nav-checklists"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.feedback.read">
              <SidebarNavLink
                to="/dashboard/zpetna-vazba"
                label="Zpětná vazba"
                icon={MessageSquare}
                testId="sidebar-nav-zpetna-vazba"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.products.read">
              <SidebarNavLink
                to="/dashboard/produkty"
                label="Produkty"
                icon={Package}
                testId="sidebar-nav-produkty"
                onNavigate={onNavigate}
              />
            </Can>
          </SidebarSection>

          <SidebarSection label="Klienti">
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/klienti"
                label="Kontakty"
                icon={Users}
                testId="sidebar-nav-klienti"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/firmy"
                label="Firmy"
                icon={Building2}
                testId="sidebar-nav-firmy"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.prihlasky.read">
              <SidebarNavLink
                to="/dashboard/prihlasky"
                label="Přihlášky"
                icon={ClipboardList}
                testId="sidebar-nav-prihlasky"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/dokumenty"
                label="Dokumenty"
                icon={FolderOpen}
                testId="sidebar-nav-dokumenty"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/marketing/tags"
                label="Štítky"
                icon={Tag}
                testId="sidebar-nav-tags"
                onNavigate={onNavigate}
              />
            </Can>
          </SidebarSection>

          <SidebarSection label="Obchod">
            <Can permission="hca.op.read">
              <SidebarNavLink
                to="/dashboard/pipeline"
                label="Pipeline"
                icon={GitBranch}
                testId="sidebar-nav-pipeline"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/aktivity"
                label="Aktivity"
                icon={CalendarClock}
                testId="sidebar-nav-aktivity"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/programy"
                label="Programy"
                icon={Box}
                testId="sidebar-nav-programy"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.provize.read_own">
              <SidebarNavLink
                to="/dashboard/provize"
                label="Provize"
                icon={Coins}
                testId="sidebar-nav-provize"
                onNavigate={onNavigate}
              />
            </Can>
          </SidebarSection>

          <SidebarSection label="E-maily">
            <Can permission="hca.email_serialy.write">
              <SidebarNavLink
                to="/dashboard/marketing/email-templates"
                label="Šablony"
                icon={Mail}
                testId="sidebar-nav-email-templates"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.email_serialy.write">
              <SidebarNavLink
                to="/dashboard/marketing/email-campaigns"
                label="Kampaně"
                icon={Send}
                testId="sidebar-nav-email-campaigns"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.email_serialy.write">
              <SidebarNavLink
                to="/dashboard/marketing/email-serialy"
                label="Automatické série"
                icon={Zap}
                testId="sidebar-nav-email-serialy"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.email_serialy.write">
              <SidebarNavLink
                to="/dashboard/marketing/email-analytics"
                label="Statistiky e-mailů"
                icon={TrendingUp}
                testId="sidebar-nav-email-analytics"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/marketing/komunikace"
                label="Komunikace"
                icon={MessageSquare}
                testId="sidebar-nav-komunikace"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.crm.read">
              <SidebarNavLink
                to="/dashboard/kontakt"
                label="Zprávy z webu"
                icon={Inbox}
                testId="sidebar-nav-kontakt"
                onNavigate={onNavigate}
              />
            </Can>
            <Can permission="hca.lead_magnet.read">
              <SidebarNavLink
                to="/dashboard/marketing/lead-magnet"
                label="Lead magnet"
                icon={Compass}
                testId="sidebar-nav-lead-magnet"
                onNavigate={onNavigate}
              />
            </Can>
          </SidebarSection>

          <SidebarSection label="Finance">
            <Can permission="hca.faktury.read">
              <SidebarNavLink
                to="/dashboard/faktury"
                label="Faktury"
                icon={Banknote}
                testId="sidebar-nav-faktury"
                onNavigate={onNavigate}
              />
            </Can>
            {/* Jirka backend merge — ekonomika seminářů (P&L). */}
            <Can permission="hca.faktury.read">
              <SidebarNavLink
                to="/dashboard/analytics/ekonomika"
                label="Ekonomika seminářů"
                icon={LineChart}
                testId="sidebar-nav-ekonomika"
                onNavigate={onNavigate}
              />
            </Can>
          </SidebarSection>

          <SidebarSection label="Tým">
            <Can permission="hca.products.read">
              <SidebarNavLink
                to="/dashboard/lektori"
                label="Lektoři"
                icon={GraduationCap}
                testId="sidebar-nav-lektori"
                onNavigate={onNavigate}
              />
            </Can>
          </SidebarSection>

          <Can permission="admin.panel.access">
            <SidebarSection label="Administrace">
              <SidebarNavLink
                to="/dashboard/admin/users"
                label="Uživatelé"
                icon={Users2}
                testId="sidebar-nav-admin-users"
                onNavigate={onNavigate}
              />
              <Can permission="audit.read">
                <SidebarNavLink
                  to="/dashboard/admin/audit"
                  label="Audit log"
                  icon={History}
                  testId="sidebar-nav-admin-audit"
                  onNavigate={onNavigate}
                />
              </Can>
              <SidebarNavLink
                to="/dashboard/admin/queues"
                label="Fronty úloh"
                icon={ListChecks}
                testId="sidebar-nav-admin-queues"
                onNavigate={onNavigate}
              />
              <SidebarNavLink
                to="/dashboard/admin/nastroje"
                label="Nástroje"
                icon={FileText}
                testId="sidebar-nav-admin"
                onNavigate={onNavigate}
              />
              <Can permission="settings.write">
                <SidebarNavLink
                  to="/dashboard/admin/mcp"
                  label="MCP"
                  icon={Plug}
                  testId="sidebar-nav-admin-mcp"
                  onNavigate={onNavigate}
                />
              </Can>
            </SidebarSection>
          </Can>
        </nav>

        {/* User card */}
        <div
          className="border-t border-[hsl(var(--sidebar-divider))] px-3 py-3"
          data-testid="app-sidebar-user"
        >
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Link
                to="/dashboard/nastaveni"
                onClick={onNavigate}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-[hsl(var(--sidebar-active-bg))] font-semibold text-[hsl(var(--sidebar-active-fg))] text-sm"
                aria-label="Nastavení účtu"
                title={displayName || "Nastavení účtu"}
                data-testid="app-sidebar-settings"
              >
                {displayName ? displayName.charAt(0).toUpperCase() : "?"}
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--sidebar-fg-muted))] transition-colors hover:bg-[hsl(var(--sidebar-bg-elevated))] hover:text-[hsl(var(--sidebar-fg))]"
                aria-label="Odhlásit se"
                title="Odhlásit se"
                data-testid="app-sidebar-logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-md bg-[hsl(var(--sidebar-bg-elevated))] px-3 py-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--sidebar-active-bg))] text-sm font-semibold text-[hsl(var(--sidebar-active-fg))]">
                {displayName ? displayName.charAt(0).toUpperCase() : "?"}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className="truncate text-sm font-medium text-[hsl(var(--sidebar-fg))]"
                  data-testid="app-sidebar-user-name"
                >
                  {displayName || "—"}
                </div>
                <div
                  className="truncate text-[0.7rem] text-[hsl(var(--sidebar-fg-muted))]"
                  data-testid="app-sidebar-user-role"
                >
                  {roleLabel}
                </div>
              </div>
              <Link
                to="/dashboard/nastaveni"
                onClick={onNavigate}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[hsl(var(--sidebar-fg-muted))] transition-colors hover:bg-[hsl(var(--sidebar-bg))] hover:text-[hsl(var(--sidebar-fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sidebar-active-bg))]"
                aria-label="Nastavení účtu"
                data-testid="app-sidebar-settings"
              >
                <Settings className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[hsl(var(--sidebar-fg-muted))] transition-colors hover:bg-[hsl(var(--sidebar-bg))] hover:text-[hsl(var(--sidebar-fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--sidebar-active-bg))]"
                aria-label="Odhlásit se"
                data-testid="app-sidebar-logout"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </aside>
    </SidebarCollapsedContext.Provider>
  );
}
